import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  alpha,
  Divider,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  Snackbar,
} from '@mui/material';
import { useUnityBuildStore } from '../stores/store';
import { ObjectService as objs } from '../api/ObjectService';
import { useStore } from 'zustand';
import { UserScaleList } from '../api/protosCompiled/object/object_pb';
import { useFetchObjects } from '../hooks/objectHooks';
import CloseIcon from '@mui/icons-material/Close';
import { pixelResolution } from './svgElements/SVGMeasurement';
import { useKeyBoardEventStore } from '../stores/keyEventStore';

// Tab panel component for switching between scale entry methods
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  // Add stopPropagation to the tabpanel
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scale-tabpanel-${index}`}
      aria-labelledby={`scale-tab-${index}`}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      {...other}
      style={{ padding: '16px 0' }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

// Component to render calibration points with enhanced visibility
// const CalibrationPointsOverlay: React.FC<{ isCalibrating: boolean }> = ({ isCalibrating }) => {
//   const theme = useTheme();
//   const measuredPoints = useStore(useUnityBuildStore).measuredPoints;
//   const pdfDimensions = useStore(useUnityBuildStore).pdfDimensions;
//   const { cursorPosition } = useStore(useUnityBuildStore);

//   if (measuredPoints.length === 0 && !isCalibrating) return null;

//   return (
//     <svg
//       style={{
//         position: 'absolute',
//         top: 0,
//         left: 0,
//         width: `${pdfDimensions.width}px`,
//         height: `${pdfDimensions.height}px`,
//         pointerEvents: 'none', // Don't interfere with interactions
//         zIndex: 10, // Higher than the main SVG but below the UI
//       }}
//     >
//       {/* Render existing calibration points */}
//       {measuredPoints.map((point, i) => (
//         <g key={`calibration-point-${i}`}>
//           {/* Outer glow effect */}
//           <circle cx={point.x} cy={point.y} r={15} fill={alpha(theme.palette.primary.main, 0.2)} />

//           {/* Outer ring */}
//           <circle
//             cx={point.x}
//             cy={point.y}
//             r={12}
//             fill="transparent"
//             stroke={theme.palette.primary.main}
//             strokeWidth={2}
//           />

//           {/* Inner solid circle */}
//           <circle
//             cx={point.x}
//             cy={point.y}
//             r={6}
//             fill={theme.palette.primary.main}
//             stroke="white"
//             strokeWidth={2}
//           />

//           {/* Point number label */}
//           <text
//             x={point.x}
//             y={point.y - 18}
//             fontSize="14"
//             fontWeight="bold"
//             fill={theme.palette.primary.main}
//             textAnchor="middle"
//             strokeWidth={0.5}
//             stroke="white"
//             paintOrder="stroke"
//           >
//             {i + 1}
//           </text>
//         </g>
//       ))}
//       {/* If we have 2 points, draw a line between them */}
//       {measuredPoints.length === 2 && (
//         <line
//           x1={measuredPoints[0].x}
//           y1={measuredPoints[0].y}
//           x2={measuredPoints[1].x}
//           y2={measuredPoints[1].y}
//           stroke={theme.palette.primary.main}
//           strokeWidth={2}
//           strokeDasharray="5,5"
//         />
//       )}
//       Show a "click here" indicator during calibration
//       {isCalibrating && cursorPosition && (
//         <g>
//           {/* Target rings */}
//           <circle
//             cx={cursorPosition.x}
//             cy={cursorPosition.y}
//             r={20}
//             fill="transparent"
//             stroke={theme.palette.info.main}
//             strokeWidth={1.5}
//             strokeDasharray="4,4"
//           >
//             <animate attributeName="r" from="20" to="30" dur="1.5s" repeatCount="indefinite" />
//             <animate attributeName="opacity" from="1" to="0" dur="1.5s" repeatCount="indefinite" />
//           </circle>
//           <circle
//             cx={cursorPosition.x}
//             cy={cursorPosition.y}
//             r={10}
//             fill="transparent"
//             stroke={theme.palette.info.main}
//             strokeWidth={1.5}
//           />

//           {/* Helper text */}
//           <text
//             x={cursorPosition.x + 25}
//             y={cursorPosition.y - 15}
//             fontSize="14"
//             fill={theme.palette.info.main}
//             textAnchor="start"
//             fontWeight="bold"
//             strokeWidth={0.5}
//             stroke="white"
//             paintOrder="stroke"
//           >
//             {measuredPoints.length === 0
//               ? 'Click to place first point'
//               : 'Click to place second point'}
//           </text>
//         </g>
//       )}
//     </svg>
//   );
// };

export const CustomScale = () => {
  const theme = useTheme();
  const selectedObject = useStore(useUnityBuildStore).selectedObject;
  const setUserScale = useStore(useUnityBuildStore).setUserScale;
  const { refetchObjects } = useFetchObjects();

  // Use the measurement-related state from UnityBuildStore
  const setMeasuredPoints = useStore(useUnityBuildStore).setMeasuredPoints;
  const measuredPoints = useStore(useUnityBuildStore).measuredPoints;

  // Get keyboard event store to enable/disable measuring
  const { setIsMeasuring, isMeasuring } = useStore(useKeyBoardEventStore);

  // State for the active tab
  const [tabValue, setTabValue] = useState(0);

  // State for manual scale entry
  const [customScale, setCustomScale] = useState('');
  const [customUnit, setCustomUnit] = useState('ft');

  // State for two-point calibration
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [knownDistance, setKnownDistance] = useState('');
  const [knownDistanceUnit, setKnownDistanceUnit] = useState('ft');

  // Success feedback
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // When component mounts, save original isMeasuring state to restore later
  const originalIsMeasuringRef = React.useRef(isMeasuring);
  useEffect(() => {
    originalIsMeasuringRef.current = isMeasuring;
    return () => {
      // Restore original isMeasuring state when component unmounts
      setIsMeasuring(originalIsMeasuringRef.current);
      setMeasuredPoints([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add a useEffect to monitor measuredPoints and stop measuring mode when we have 2 points
  useEffect(() => {
    // If we've placed exactly 2 points while calibrating, stop the measuring mode
    // but don't clear the points - we want to keep them visible
    if (isCalibrating && measuredPoints.length === 2) {
      // Stop capturing new points, but leave the measuring UI active for visual feedback
      setIsCalibrating(false);
      setIsMeasuring(false);

      // Show success message for the user
      setSuccessMessage('Points placed successfully! Now enter the known distance.');
      setShowSuccessMessage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredPoints.length, isCalibrating]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset calibration when switching tabs
    if (newValue === 1) {
      setMeasuredPoints([]);
      setKnownDistance('');
    }
  };

  // Handle manual scale submission
  const handleManualSubmit = async () => {
    if (!selectedObject) return;
    const scaleValue = parseFloat(customScale);

    if (isNaN(scaleValue) || scaleValue <= 0) {
      alert('Please enter a valid positive number for the scale.');
      return;
    }

    // Set the scale value directly
    let standardizedScale: number;
    if (customUnit === 'ft') {
      standardizedScale = scaleValue; // For "1" = X ft", scale is X
    } else {
      // For inches
      standardizedScale = scaleValue / 12; // Convert inches to feet (1" = X" is 1" = X/12 ft)
    }

    try {
      // Update the scale on the server
      await updateObjectScale(standardizedScale);
      setCustomScale('');

      // Show success message
      setSuccessMessage(`Scale set successfully: 1" = ${scaleValue} ${customUnit}`);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Failed to update scale:', error);
      alert('Failed to update scale. Please try again.');
    }
  };

  // Start calibration mode by enabling measuring
  const handleStartCalibration = () => {
    // Clear any existing measurements
    setMeasuredPoints([]);
    // Enable measuring mode
    setIsMeasuring(true);
    setIsCalibrating(true);
  };

  // Modify the cleanupCalibration function to leave points visible if we have 2 points and are submitting scale
  const cleanupCalibration = (preservePoints = false) => {
    setIsCalibrating(false);
    setIsMeasuring(false);

    // Only clear measured points if we're not preserving them
    if (!preservePoints) {
      setMeasuredPoints([]);
    }

    setKnownDistance('');
  };

  // Handle calibration submit
  const handleCalibrationSubmit = async () => {
    // We need exactly 2 measured points
    if (measuredPoints.length !== 2 || !knownDistance || !selectedObject) {
      alert('Please draw two points and enter a known distance.');
      return;
    }

    const distance = parseFloat(knownDistance);
    if (isNaN(distance) || distance <= 0) {
      alert('Please enter a valid positive number for the distance.');
      return;
    }

    // Calculate pixel distance between points
    const pixelDistance = Math.sqrt(
      Math.pow(measuredPoints[1].x - measuredPoints[0].x, 2) +
        Math.pow(measuredPoints[1].y - measuredPoints[0].y, 2)
    );

    // Convert pixel distance to inch distance
    const inchDistance = pixelDistance / pixelResolution;

    // Calculate the scale factor
    let realWorldDistanceInFeet: number;
    if (knownDistanceUnit === 'ft') {
      realWorldDistanceInFeet = distance;
    } else {
      // Convert inches to feet
      realWorldDistanceInFeet = distance / 12;
    }

    // Calculate scale as 1" = X ft where X is realWorldDistanceInFeet / inchDistance
    const scaleValue = realWorldDistanceInFeet / inchDistance;

    try {
      // Update the scale on the server
      await updateObjectScale(scaleValue);

      // Clean up but preserve the measurement points
      cleanupCalibration(true);

      // Show a success message with more detailed information
      const scaleRatio = Math.round(scaleValue * 12);

      setSuccessMessage(
        `Scale set successfully! 
        1" = ${scaleValue.toFixed(2)}' (1:${scaleRatio})
        The measured distance is ${inchDistance.toFixed(2)}" on the drawing, which equals ${distance} ${knownDistanceUnit} in real-world scale.`
      );
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Failed to update scale:', error);
      alert('Failed to update scale. Please try again.');
    }
  };

  // Common function to update object scale
  const updateObjectScale = async (scaleValue: number) => {
    if (!selectedObject) return;

    const userScaleList = selectedObject.userscalesList;
    const currentPage = userScaleList.length > 0 ? userScaleList[0].pagenumber : 1;

    const newScale: UserScaleList.AsObject = {
      pagenumber: currentPage,
      scale: scaleValue,
    };

    // Create or update the scale list
    const updatedScaleList =
      userScaleList.length > 0
        ? userScaleList.map(scale => (scale.pagenumber === currentPage ? newScale : scale))
        : [newScale];

    // Update the scale on the server
    await objs.updateObjectScale(selectedObject.id, updatedScaleList);

    // Update the local state
    setUserScale(scaleValue);

    // Refetch objects to update the state
    refetchObjects();

    // CRITICAL: This event must be dispatched to trigger takeoff assembly recalculation.
    // The ToolBar component listens for this event and updates all assemblies with the new scale.
    // Removing this would break scale-dependent measurements across the application.
    document.dispatchEvent(
      new CustomEvent('custom-scale-updated', {
        detail: { scale: scaleValue },
      })
    );
  };

  // Handle close when the user is done
  const handleClose = () => {
    // Reset measuring state and clean up
    cleanupCalibration();

    // Restore original measuring state
    setIsMeasuring(originalIsMeasuringRef.current);

    // Dispatch a custom event that will be caught by the ToolBar component
    document.dispatchEvent(new CustomEvent('close-custom-scale'));
  };

  // Function to prevent event propagation
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      {/* Render the calibration points overlay when calibrating or points are visible */}
      {/* {(isCalibrating || measuredPoints.length > 0) && tabValue === 1 && (
        <CalibrationPointsOverlay isCalibrating={isCalibrating} />
      )} */}

      <Paper
        elevation={4}
        className="custom-scale-container"
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        sx={{
          position: 'fixed',
          top: '120px',
          left: '25px',
          width: '400px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 10000,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '8px',
          boxShadow: theme.shadows[8],
          transition: 'all 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          }}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
        >
          <Typography variant="h6" fontWeight={500} color="primary">
            Custom Scale
          </Typography>
          <IconButton
            onClick={e => {
              stopPropagation(e);
              handleClose();
            }}
            size="small"
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ padding: '20px' }} onClick={stopPropagation} onMouseDown={stopPropagation}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              stopPropagation(e);
              handleTabChange(e, newValue);
            }}
            variant="fullWidth"
            sx={{
              mb: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Manual Entry" onClick={stopPropagation} onMouseDown={stopPropagation} />
            <Tab
              label="Two-Point Calibration"
              onClick={stopPropagation}
              onMouseDown={stopPropagation}
            />
          </Tabs>

          {/* Manual Entry Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Enter a custom scale ratio manually (e.g., 1" = 40' or 1" = 24").
            </Typography>

            <Box
              sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
              onClick={stopPropagation}
              onMouseDown={stopPropagation}
            >
              <Typography variant="body1" sx={{ mr: 2 }}>
                1"
              </Typography>
              <Typography variant="body1" sx={{ mr: 2 }}>
                =
              </Typography>
              <TextField
                value={customScale}
                onChange={e => setCustomScale(e.target.value)}
                placeholder="e.g., 40"
                size="small"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                sx={{ width: '100px', mr: 2 }}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
              />
              <FormControl
                size="small"
                sx={{ width: '80px' }}
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
              >
                <Select
                  value={customUnit}
                  onChange={(e: SelectChangeEvent) => setCustomUnit(e.target.value)}
                  onClick={stopPropagation}
                  onMouseDown={stopPropagation}
                >
                  <MenuItem value="ft" onClick={stopPropagation} onMouseDown={stopPropagation}>
                    ft
                  </MenuItem>
                  <MenuItem value="in" onClick={stopPropagation} onMouseDown={stopPropagation}>
                    in
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={e => {
                stopPropagation(e);
                handleManualSubmit();
              }}
              disabled={!customScale || parseFloat(customScale) <= 0}
              fullWidth
              sx={{ mt: 2 }}
            >
              Apply Scale
            </Button>
          </TabPanel>

          {/* Two-Point Calibration Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              }}
            >
              <Typography variant="subtitle2" color="info.main" gutterBottom>
                How Two-Point Calibration Works
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                1. Place two points on your drawing at a known distance from each other (like a wall
                or door with a standard dimension)
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                2. Enter the real-world distance between those points
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                3. The system will calculate the scale based on the pixel distance vs. real-world
                distance
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 3 }}>
              Draw two points on the document and enter the known real-world distance between them.
            </Typography>

            <Box sx={{ mb: 3 }} onClick={stopPropagation} onMouseDown={stopPropagation}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Step 1: Draw measurement points
              </Typography>
              <Button
                variant="outlined"
                color={isCalibrating ? 'error' : 'primary'}
                onClick={e => {
                  stopPropagation(e);
                  if (isCalibrating) {
                    cleanupCalibration();
                  } else {
                    handleStartCalibration();
                  }
                }}
                sx={{ mb: 2 }}
                fullWidth
              >
                {isCalibrating ? 'Cancel Drawing' : 'Start Drawing'}
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="body2">
                  Points drawn: {measuredPoints.length}/2
                  {measuredPoints.length === 2 && (
                    <Typography component="span" color="success.main" ml={1}>
                      ✓
                    </Typography>
                  )}
                </Typography>
                {measuredPoints.length > 0 && (
                  <Button
                    size="small"
                    onClick={e => {
                      stopPropagation(e);
                      setMeasuredPoints([]);
                      // If we have points and we clear them, we should enable calibration mode again
                      setIsCalibrating(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear Points
                  </Button>
                )}
              </Box>

              {measuredPoints.length === 2 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Two points successfully placed! Now enter the known distance.
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 3 }} onClick={stopPropagation} onMouseDown={stopPropagation}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Step 2: Enter known distance
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  value={knownDistance}
                  onChange={e => setKnownDistance(e.target.value)}
                  placeholder="e.g., 10"
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                  disabled={measuredPoints.length !== 2}
                  sx={{ width: '120px', mr: 2 }}
                  onClick={stopPropagation}
                  onMouseDown={stopPropagation}
                />
                <FormControl
                  size="small"
                  sx={{ width: '80px' }}
                  onClick={stopPropagation}
                  onMouseDown={stopPropagation}
                >
                  <Select
                    value={knownDistanceUnit}
                    onChange={(e: SelectChangeEvent) => setKnownDistanceUnit(e.target.value)}
                    disabled={measuredPoints.length !== 2}
                    onClick={stopPropagation}
                    onMouseDown={stopPropagation}
                  >
                    <MenuItem value="ft" onClick={stopPropagation} onMouseDown={stopPropagation}>
                      ft
                    </MenuItem>
                    <MenuItem value="in" onClick={stopPropagation} onMouseDown={stopPropagation}>
                      in
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={e => {
                stopPropagation(e);
                handleCalibrationSubmit();
              }}
              disabled={
                measuredPoints.length !== 2 || !knownDistance || parseFloat(knownDistance) <= 0
              }
              fullWidth
              sx={{ mt: 2 }}
            >
              Calculate & Apply Scale
            </Button>
          </TabPanel>
        </Box>

        {/* Success message snackbar */}
        <Snackbar
          open={showSuccessMessage}
          autoHideDuration={4000}
          onClose={() => setShowSuccessMessage(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </>
  );
};
